import { Button } from '@design-systems/canvas-ui'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ErrorBoundary } from 'react-error-boundary'
import { MPMLayout } from 'components/layout/layout'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useGetBrands } from 'hooks/brands/useBrands'
import { useEffect, useState } from 'react'
import { BasicPlaceholder } from 'components/placeholder'
import useCountdown from 'hooks/utils/useCountdown'
import './brands.css'
import Icon, { ChevronLeftIcon } from '@design-systems/react-icons'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import { getTrimmedBrandName } from './helpers'
import { useLogClick } from 'utils/analytics'
export type Brand = {
    launch_date: string
    start_date: string
    end_date: string
    brand_id: string
    brand_name: string
    brand_category: string
    intraday_flag: boolean
    brand_ref_id_arr: string[]
    image_url: string
    brand_mic_licensor: string
}

export const Brands = () => {
    const navigate = useNavigate()
    const { brands } = TRANSLATION_KEYS
    const [searchParams, setSearchParams] = useSearchParams()
    const { t, currentLocation, logout, env, session } = useEssentials()
    const [brand, setBrand] = useState<Brand | null>(null)
    const { logClick } = useLogClick('brands')
    const { data, isLoading, isError, error, refetch } = useGetBrands({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    useEffect(() => {
        const brandFromUrl = searchParams.get('brand')
        if (brandFromUrl) {
            const parsedBrand = JSON.parse(decodeURIComponent(brandFromUrl))
            setBrand(parsedBrand)
        }
    }, [searchParams])

    const handleBrandChange = (selectedBrand: Brand) => {
        setBrand(selectedBrand)
        const trimmedBrandName = getTrimmedBrandName(selectedBrand.brand_name)

        const serializedBrand = encodeURIComponent(
            JSON.stringify(selectedBrand),
        )
        void navigate(
            `/brands/${trimmedBrandName}?locationId=${currentLocation}`,
        )
        setSearchParams({
            brand: serializedBrand,
            locationId: currentLocation,
        })
    }

    const count = useCountdown(5, logout, error as Error)
    const BrandsBackButton = () => {
        return (
            <Button
                isIconOnly
                aria-label={t(brands.goBackAriaLabel)}
                onClick={() => {
                    logClick('brands back button')
                    setBrand(null)
                    void navigate(`/brands?locationId=${currentLocation}`)
                }}
                variant="ghost"
            >
                <Icon
                    svg={ChevronLeftIcon}
                    className="navbar-icon"
                    size="lg"
                    aria-hidden
                />
            </Button>
        )
    }
    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(brands.title)}
                />
            )}
        >
            <MPMLayout
                heading={brand ? brand.brand_name : t(brands.title)}
                className="hc8-ph-3x"
                isMenuOverride={brand ? true : false}
                menuOverride={<BrandsBackButton />}
            >
                {isLoading ? (
                    <div className="placeholder-container">
                        <BasicPlaceholder type="text" rows={1} />
                        <BasicPlaceholder type="table" rows={5} columns={3} />
                    </div>
                ) : isError || !data ? (
                    <ErrorAlert error={error} count={count} onRetry={refetch} />
                ) : (
                    <Outlet
                        context={{
                            data,
                            brand,
                            setBrand,
                            handleBrandSelect: (selectedBrand: Brand) => {
                                handleBrandChange(selectedBrand)
                            },
                        }}
                    />
                )}
            </MPMLayout>
        </ErrorBoundary>
    )
}
